/**
 * The error / warning / note split as one bar plus a legend.
 *
 * Its job is to stop "10 warnings" reading like ten separate problems when it
 * is one problem seen across ten years — the counts are findings, the issue
 * list above is distinct problems, and the bar is what reconciles them.
 *
 * The track is dropped when only one severity is present: a chain scoring 0.94
 * with three warnings and nothing else drew a solid amber bar edge to edge,
 * which reads as a far worse result than "3 minor issues found". With nothing
 * to split there is no proportion to show.
 */

import Tooltip from '../../ui/Tooltip';
import type { IssueSeverity, ValidationSummary } from '../../../types/dataQuality';
import { SEVERITY_ORDER, SEVERITY_TOKENS } from './dqTokens';

export interface SeverityBarProps {
  summary: ValidationSummary;
  /** Legend text size; the console uses `md`, the Overview strip `sm`. */
  size?: 'sm' | 'md';
}

export default function SeverityBar({ summary, size = 'sm' }: SeverityBarProps) {
  const counts: Record<IssueSeverity, number> = {
    error: summary.error_count ?? 0,
    warning: summary.warning_count ?? 0,
    info: summary.info_count ?? 0,
  };

  const segments = SEVERITY_ORDER.filter((key) => counts[key] > 0).map((key) => ({
    key,
    count: counts[key],
    token: SEVERITY_TOKENS[key],
  }));

  const total = segments.reduce((sum, s) => sum + s.count, 0);
  // A clean chain has nothing to split — the headline already says so.
  if (total === 0) return null;

  const hasSplit = segments.length > 1;
  const legendText = size === 'md' ? 'text-xs' : 'text-[11px]';

  return (
    <div>
      {hasSplit && (
        <div className="flex h-1.5 overflow-hidden rounded-full bg-neutral-100">
          {segments.map((segment) => (
            <Tooltip
              key={segment.key}
              content={`${segment.count} ${
                segment.count === 1 ? segment.token.label.toLowerCase() : segment.token.plural
              }`}
              position="top"
            >
              <div
                className={segment.token.dot}
                style={{ width: `${(segment.count / total) * 100}%` }}
              />
            </Tooltip>
          ))}
        </div>
      )}
      <div className={`flex flex-wrap gap-x-3 gap-y-0.5 ${hasSplit ? 'mt-1.5' : ''}`}>
        {segments.map((segment) => (
          <span
            key={segment.key}
            className={`flex items-center gap-1 text-neutral-500 ${legendText}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${segment.token.dot}`} />
            {segment.count}{' '}
            {segment.count === 1 ? segment.token.label.toLowerCase() : segment.token.plural}
          </span>
        ))}
      </div>
    </div>
  );
}
